/**
 * Hand-rolled LSP ↔ Monaco bridge for the workbench editor.
 *
 * Talks JSON-RPC to the ArchLang LSP worker directly and maps each request to a
 * `monaco.languages.register*Provider` call — no monaco-languageclient (its
 * vscode-api emulator never reaches Monaco's standalone provider registry; both
 * the site and Studio hit that dead end). ~one provider per LSP capability the
 * server advertises.
 *
 * The worker is created by an injected `createWorker()` factory (the build-tool-
 * specific `new Worker(new URL(...))` stays in the consuming app — Vite
 * `?worker`, esbuild prebuild, etc.).
 *
 * Multi-composition on one page (the reuse mandate). Monaco keeps ONE model per
 * URI globally and providers are registered per-language globally, so two
 * editors that both edit `/main.arch` would share a buffer and merge each
 * other's completions. The bridge prevents that with a per-composition URI
 * AUTHORITY (`wbId`):
 *   - editor models live at `memfs://<wbId>/<path>` (distinct per composition);
 *   - every provider + the document watcher GATES on `uri.authority === wbId`,
 *     so a bridge ignores other compositions' models entirely;
 *   - at the worker boundary the authority is stripped, so each worker still
 *     sees a normal single-workspace `memfs:///<path>` world (the LSP server is
 *     unchanged), and incoming worker URIs are re-namespaced back to the model.
 * Net: N live bridges = N isolated workers, no cross-contamination.
 *
 * Lifecycle: `createLspBridge()` is an INSTANCE (one per Editor), each owning
 * its own worker — disposed on editor unmount, and torn down even if dispose
 * lands mid-boot. `start()` boots on first call and RESEEDS on later calls
 * (`arch/setWorkspace` + a watched-files nudge) so a re-mount or file-set change
 * doesn't resolve against stale siblings.
 */
import type * as MonacoNs from "monaco-editor";
import { isMarkdownPath } from "@archlang/viewer-core";
import {
  BrowserMessageReader,
  BrowserMessageWriter,
  createMessageConnection,
  type MessageConnection,
} from "vscode-jsonrpc/browser.js";
import type {
  CompletionItem,
  CompletionList,
  CompletionParams,
  CodeAction,
  CodeActionParams,
  Command,
  DefinitionParams,
  Diagnostic,
  DocumentFormattingParams,
  DocumentLink,
  DocumentLinkParams,
  DocumentRangeFormattingParams,
  DocumentSymbol,
  DocumentSymbolParams,
  FoldingRange,
  FoldingRangeParams,
  Hover,
  HoverParams,
  InitializeResult,
  Location,
  LocationLink,
  PrepareRenameParams,
  PublishDiagnosticsParams,
  Range as LSPRange,
  ReferenceParams,
  RenameParams,
  SelectionRange,
  SelectionRangeParams,
  SemanticTokens,
  SemanticTokensParams,
  SignatureHelp,
  SignatureHelpParams,
  SymbolInformation,
  TextEdit,
  WorkspaceEdit,
} from "vscode-languageserver-protocol";

/** Factory the consuming app provides — keeps the worker URL in its bundler. */
export type CreateWorker = () => Worker;

export interface BridgeHandle {
  readonly worker: Worker;
  readonly connection: MessageConnection;
  /**
   * Push one file's content into the worker's VFS + recompile (W1, WYSIWYG
   * plan §6). The route for files WITHOUT a live Monaco model — those never
   * `didOpen`, and the server drops `didChange` for unopened URIs, so a
   * closed-file store edit (board/inspector mutation, journal undo) would
   * leave the worker stale and a later rename would compute corrupt edits.
   * MERGE semantics (`replace: false`) — a full-replace here would wipe every
   * other file the worker knows (the `reseedWorkspace` trap).
   */
  syncClosedFile(path: string, content: string): Promise<void>;
  dispose(): void;
}

const LANGUAGE_ID = "archlang";
const URI_SCHEME = "memfs";
const ROOT_URI = "memfs:///";
const INIT_TIMEOUT_MS = 10_000;
// Completion trigger characters are the SERVER's to define — the bridge derives
// them from the InitializeResult (P2b) so a server-side change (e.g. `"` for the
// in-string slots) reaches the Monaco surface without editing this file. This
// fallback only guards the case where the capability is absent; keep it in sync
// with server-core.ts `completionProvider.triggerCharacters`.
const DEFAULT_COMPLETION_TRIGGERS = [".", " ", ">", "/", "#", "?", '"'];
// Monaco marker owner for LSP-published diagnostics — the sole diagnostics source.
const LSP_MARKER_OWNER = "archlang-lsp";

// ----- URI namespacing (editor model URI ↔ worker URI) ---------------------
// Editor model:  memfs://<wbId>/<path>   (authority = composition id)
// Worker:        memfs:///<path>         (authority empty — the LSP's own world)

/** Worker-side URI string for a model path (`/main.arch` → `memfs:///main.arch`). */
export function workerUriForPath(path: string): string {
  return ROOT_URI + path.replace(/^\//, "");
}
/** The `/path` part of a worker URI (`memfs:///main.arch` → `/main.arch`). */
export function pathFromWorkerUri(workerUri: string): string {
  const p = workerUri.replace(/^memfs:\/\//, "");
  return p.startsWith("/") ? p : "/" + p;
}
/** Model URI string for a composition + path (used by the Editor). */
export function modelUriString(wbId: string, path: string): string {
  return `${URI_SCHEME}://${wbId}/${path.replace(/^\//, "")}`;
}

function withTimeout<T>(p: Promise<T>, ms: number, msg: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(msg)), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

// ----- Position / Range converters (Monaco 1-based ↔ LSP 0-based) ----------
type Position = { line: number; character: number };
export function p2m(p: Position): MonacoNs.IPosition {
  return { lineNumber: p.line + 1, column: p.character + 1 };
}
export function r2m(r: LSPRange): MonacoNs.IRange {
  return {
    startLineNumber: r.start.line + 1,
    startColumn: r.start.character + 1,
    endLineNumber: r.end.line + 1,
    endColumn: r.end.character + 1,
  };
}
export function m2p(pos: MonacoNs.IPosition): Position {
  return { line: pos.lineNumber - 1, character: pos.column - 1 };
}
export function m2r(r: MonacoNs.IRange): LSPRange {
  return {
    start: { line: r.startLineNumber - 1, character: r.startColumn - 1 },
    end: { line: r.endLineNumber - 1, character: r.endColumn - 1 },
  };
}

async function bootBridge(
  monaco: typeof MonacoNs,
  createWorker: CreateWorker,
  wbId: string,
  initialFiles: Record<string, string>,
  /** Errors-only mode: keep only Error markers — TODO + graph warnings are noise on
   *  a quick sketch. */
  errorsOnly: boolean,
): Promise<BridgeHandle> {
  const worker = createWorker();
  worker.addEventListener("error", (e) => {
    console.error("[arch-lsp] worker error", e.message, e);
  });

  // This bridge owns models whose URI authority is its composition id; it
  // ignores everything else (other compositions' editors on the same page).
  const owns = (model: MonacoNs.editor.ITextModel): boolean =>
    model.uri.scheme === URI_SCHEME && model.uri.authority === wbId;
  const toWorker = (uri: MonacoNs.Uri): string => workerUriForPath(uri.path);
  const toModel = (workerUri: string): MonacoNs.Uri =>
    monaco.Uri.from({ scheme: URI_SCHEME, authority: wbId, path: pathFromWorkerUri(workerUri) });

  const reader = new BrowserMessageReader(worker);
  const writer = new BrowserMessageWriter(worker);
  const connection = createMessageConnection(reader, writer);
  connection.listen();

  // Set from the InitializeResult below, then consumed when the completion
  // provider registers (both happen after `initialize` resolves).
  let completionTriggers: string[] = DEFAULT_COMPLETION_TRIGGERS;

  // A worker that fails to boot (bad bundle, CSP, throw on start) must not leave
  // the boot hanging forever — initialize is raced against a timeout, and any
  // failure tears the worker down so the caller's bootPromise rejects cleanly.
  try {
    // Seed the worker VFS BEFORE initialize so the package walk finds
    // package.archspace + stdlib (paths like /__stdlib__/arch.extras/…).
    if (Object.keys(initialFiles).length > 0) {
      await connection.sendNotification("arch/setWorkspace", {
        files: initialFiles,
        replace: true,
      });
    }

    const init: InitializeResult = await withTimeout(
      connection.sendRequest("initialize", {
        processId: null,
        rootUri: ROOT_URI,
        capabilities: {
          textDocument: {
            synchronization: { didSave: false, willSave: false },
            completion: {
              completionItem: {
                snippetSupport: true,
                // P2b: the server capability-gates these — advertise so it
                // emits `labelDetails` (parent path + kind) and
                // `InsertReplaceEdit` (replace-to-word-end on mid-word accept).
                insertReplaceSupport: true,
                labelDetailsSupport: true,
              },
            },
            hover: { contentFormat: ["markdown", "plaintext"] },
            definition: { linkSupport: true },
            rename: { prepareSupport: true },
            formatting: {},
            rangeFormatting: {},
            documentSymbol: { hierarchicalDocumentSymbolSupport: true },
            codeAction: { codeActionLiteralSupport: { codeActionKind: { valueSet: [] } } },
            foldingRange: {},
            selectionRange: {},
            documentLink: {},
            references: {},
            signatureHelp: { signatureInformation: { parameterInformation: { labelOffsetSupport: true } } },
            semanticTokens: {
              requests: { full: { delta: false }, range: true },
              tokenTypes: [],
              tokenModifiers: [],
              formats: ["relative"],
            },
          },
          workspace: { workspaceFolders: false, configuration: false },
        },
        workspaceFolders: null,
      }),
      INIT_TIMEOUT_MS,
      "LSP initialize timed out",
    );
    completionTriggers =
      init.capabilities.completionProvider?.triggerCharacters ??
      DEFAULT_COMPLETION_TRIGGERS;
    await connection.sendNotification("initialized", {});
  } catch (err) {
    connection.dispose();
    worker.terminate();
    throw err;
  }

  // Diagnostics → Monaco markers. The worker reports a worker URI; re-namespace
  // it to THIS composition's model.
  connection.onNotification(
    "textDocument/publishDiagnostics",
    (params: PublishDiagnosticsParams) => {
      const model = monaco.editor.getModel(toModel(params.uri));
      if (!model) return;
      const markers: MonacoNs.editor.IMarkerData[] = filterDiagnostics(
        params.diagnostics,
        errorsOnly,
      ).map(
        (d: Diagnostic) => ({
          severity: lspSeverityToMonaco(monaco, d.severity ?? 1),
          message: d.message,
          source: typeof d.source === "string" ? d.source : LSP_MARKER_OWNER,
          code: d.code !== undefined ? String(d.code) : undefined,
          startLineNumber: d.range.start.line + 1,
          startColumn: d.range.start.character + 1,
          endLineNumber: d.range.end.line + 1,
          endColumn: d.range.end.character + 1,
        }),
      );
      monaco.editor.setModelMarkers(model, LSP_MARKER_OWNER, markers);
    },
  );

  // Document sync — watch only THIS composition's models.
  const versions = new WeakMap<MonacoNs.editor.ITextModel, number>();
  const watchers = new Map<MonacoNs.editor.ITextModel, MonacoNs.IDisposable>();

  function startWatching(model: MonacoNs.editor.ITextModel): void {
    if (model.getLanguageId() !== LANGUAGE_ID || !owns(model)) return;
    if (watchers.has(model)) return;
    versions.set(model, 1);
    void connection.sendNotification("textDocument/didOpen", {
      textDocument: {
        uri: toWorker(model.uri),
        languageId: LANGUAGE_ID,
        version: 1,
        text: model.getValue(),
      },
    });
    const sub = model.onDidChangeContent(() => {
      const next = (versions.get(model) ?? 1) + 1;
      versions.set(model, next);
      void connection.sendNotification("textDocument/didChange", {
        textDocument: { uri: toWorker(model.uri), version: next },
        contentChanges: [{ text: model.getValue() }],
      });
    });
    const disposeSub = model.onWillDispose(() => {
      void connection.sendNotification("textDocument/didClose", {
        textDocument: { uri: toWorker(model.uri) },
      });
      watchers.get(model)?.dispose();
      watchers.delete(model);
      disposeSub.dispose();
    });
    // Store BOTH listeners so bridge teardown disposes the will-dispose watcher
    // too — otherwise it leaks and later fires against a dead connection.
    watchers.set(model, {
      dispose: () => {
        sub.dispose();
        disposeSub.dispose();
      },
    });
  }
  for (const m of monaco.editor.getModels()) startWatching(m);
  const onCreate = monaco.editor.onDidCreateModel(startWatching);

  const ldisp: MonacoNs.IDisposable[] = [onCreate];
  const td = (model: MonacoNs.editor.ITextModel) => ({ uri: toWorker(model.uri) });

  ldisp.push(monaco.languages.registerHoverProvider(LANGUAGE_ID, {
    async provideHover(model, position) {
      if (!owns(model)) return null;
      const r: Hover | null = await safe(connection.sendRequest("textDocument/hover", {
        textDocument: td(model),
        position: m2p(position),
      } satisfies HoverParams));
      if (!r) return null;
      return { contents: normalizeHoverContents(r), range: r.range ? r2m(r.range) : undefined };
    },
  }));

  ldisp.push(monaco.languages.registerCompletionItemProvider(LANGUAGE_ID, {
    triggerCharacters: completionTriggers,
    async provideCompletionItems(model, position) {
      if (!owns(model)) return { suggestions: [] };
      const r: CompletionItem[] | CompletionList | null = await safe(
        connection.sendRequest("textDocument/completion", {
          textDocument: td(model),
          position: m2p(position),
        } satisfies CompletionParams),
      );
      if (!r) return { suggestions: [] };
      const items = Array.isArray(r) ? r : r.items;
      return {
        suggestions: items.map((it) => completionItemToMonaco(monaco, it)),
        // A capped list (`isIncomplete`) must re-query as the prefix narrows, or
        // the viewer would forever show the first server page (P2b item 6).
        incomplete: Array.isArray(r) ? false : r.isIncomplete,
      };
    },
    async resolveCompletionItem(item) {
      const data = (item as { _lspData?: unknown })._lspData;
      if (data === undefined || item.documentation) return item;
      const label = typeof item.label === "string" ? item.label : item.label.label;
      const resolved: CompletionItem | null = await safe(
        connection.sendRequest("completionItem/resolve", { label, data } as CompletionItem),
      );
      if (resolved?.documentation) {
        item.documentation = resolved.documentation as MonacoNs.IMarkdownString | string;
      }
      return item;
    },
  }));

  ldisp.push(monaco.languages.registerDefinitionProvider(LANGUAGE_ID, {
    async provideDefinition(model, position) {
      if (!owns(model)) return null;
      const r: Location | Location[] | LocationLink[] | null = await safe(
        connection.sendRequest("textDocument/definition", {
          textDocument: td(model),
          position: m2p(position),
        } satisfies DefinitionParams),
      );
      if (!r) return null;
      const arr = Array.isArray(r) ? r : [r];
      return arr.map((loc) =>
        "targetUri" in loc
          ? { uri: toModel(loc.targetUri), range: r2m(loc.targetSelectionRange) }
          : { uri: toModel(loc.uri), range: r2m(loc.range) },
      );
    },
  }));

  ldisp.push(monaco.languages.registerReferenceProvider(LANGUAGE_ID, {
    async provideReferences(model, position, context) {
      if (!owns(model)) return null;
      const r: Location[] | null = await safe(connection.sendRequest("textDocument/references", {
        textDocument: td(model),
        position: m2p(position),
        context: { includeDeclaration: context.includeDeclaration },
      } satisfies ReferenceParams));
      if (!r) return null;
      return r.map((loc) => ({ uri: toModel(loc.uri), range: r2m(loc.range) }));
    },
  }));

  ldisp.push(monaco.languages.registerDocumentSymbolProvider(LANGUAGE_ID, {
    async provideDocumentSymbols(model) {
      if (!owns(model)) return [];
      const r: DocumentSymbol[] | SymbolInformation[] | null = await safe(
        connection.sendRequest("textDocument/documentSymbol", {
          textDocument: td(model),
        } satisfies DocumentSymbolParams),
      );
      if (!r) return [];
      return r.map(toMonacoSymbol);
    },
  }));

  ldisp.push(monaco.languages.registerDocumentFormattingEditProvider(LANGUAGE_ID, {
    async provideDocumentFormattingEdits(model, options) {
      if (!owns(model)) return [];
      const r: TextEdit[] | null = await safe(connection.sendRequest("textDocument/formatting", {
        textDocument: td(model),
        options: { tabSize: options.tabSize, insertSpaces: options.insertSpaces },
      } satisfies DocumentFormattingParams));
      if (!r) return [];
      return r.map(textEditToMonaco);
    },
  }));

  ldisp.push(monaco.languages.registerDocumentRangeFormattingEditProvider(LANGUAGE_ID, {
    async provideDocumentRangeFormattingEdits(model, range, options) {
      if (!owns(model)) return [];
      const r: TextEdit[] | null = await safe(connection.sendRequest("textDocument/rangeFormatting", {
        textDocument: td(model),
        range: m2r(range),
        options: { tabSize: options.tabSize, insertSpaces: options.insertSpaces },
      } satisfies DocumentRangeFormattingParams));
      if (!r) return [];
      return r.map(textEditToMonaco);
    },
  }));

  ldisp.push(monaco.languages.registerRenameProvider(LANGUAGE_ID, {
    async resolveRenameLocation(model, position) {
      if (!owns(model)) return null;
      const r: { range: LSPRange; placeholder: string } | LSPRange | null = await safe(
        connection.sendRequest("textDocument/prepareRename", {
          textDocument: td(model),
          position: m2p(position),
        } satisfies PrepareRenameParams),
      );
      if (!r) {
        return { range: { startLineNumber: position.lineNumber, startColumn: position.column, endLineNumber: position.lineNumber, endColumn: position.column }, text: "" };
      }
      if ("placeholder" in r) return { range: r2m(r.range), text: r.placeholder };
      const word = model.getWordAtPosition(position);
      return { range: r2m(r), text: word?.word ?? "" };
    },
    async provideRenameEdits(model, position, newName) {
      if (!owns(model)) return { edits: [] };
      const r: WorkspaceEdit | null = await safe(connection.sendRequest("textDocument/rename", {
        textDocument: td(model),
        position: m2p(position),
        newName,
      } satisfies RenameParams));
      if (!r) return { edits: [] };
      return workspaceEditToMonaco(r, toModel);
    },
  }));

  ldisp.push(monaco.languages.registerCodeActionProvider(LANGUAGE_ID, {
    async provideCodeActions(model, range, context) {
      if (!owns(model)) return { actions: [], dispose: noop };
      const r: (Command | CodeAction)[] | null = await safe(connection.sendRequest("textDocument/codeAction", {
        textDocument: td(model),
        range: m2r(range),
        context: {
          diagnostics: context.markers.map((m) => ({
            range: m2r(m),
            message: m.message,
            severity: monacoSeverityToLsp(m.severity),
          })),
          only: undefined,
        },
      } satisfies CodeActionParams));
      if (!r) return { actions: [], dispose: noop };
      return {
        actions: r
          .filter((a): a is CodeAction => "title" in a && !("command" in a && typeof a.command === "string"))
          .map((a) => ({
            title: a.title,
            kind: a.kind,
            isPreferred: a.isPreferred,
            edit: a.edit ? workspaceEditToMonaco(a.edit, toModel) : undefined,
          })),
        dispose: noop,
      };
    },
  }));

  ldisp.push(monaco.languages.registerFoldingRangeProvider(LANGUAGE_ID, {
    async provideFoldingRanges(model) {
      if (!owns(model)) return [];
      const r: FoldingRange[] | null = await safe(connection.sendRequest("textDocument/foldingRange", {
        textDocument: td(model),
      } satisfies FoldingRangeParams));
      if (!r) return [];
      return r.map((f) => ({
        start: f.startLine + 1,
        end: f.endLine + 1,
        kind: f.kind ? new monaco.languages.FoldingRangeKind(f.kind) : undefined,
      }));
    },
  }));

  ldisp.push(monaco.languages.registerSelectionRangeProvider(LANGUAGE_ID, {
    async provideSelectionRanges(model, positions) {
      if (!owns(model)) return [];
      const r: SelectionRange[] | null = await safe(connection.sendRequest("textDocument/selectionRange", {
        textDocument: td(model),
        positions: positions.map(m2p),
      } satisfies SelectionRangeParams));
      if (!r) return [];
      return r.map((sr) => {
        const out: MonacoNs.languages.SelectionRange[] = [];
        let cur: SelectionRange | undefined = sr;
        while (cur) {
          out.push({ range: r2m(cur.range) });
          cur = cur.parent;
        }
        return out;
      });
    },
  }));

  ldisp.push(monaco.languages.registerLinkProvider(LANGUAGE_ID, {
    async provideLinks(model) {
      if (!owns(model)) return { links: [], dispose: noop };
      const r: DocumentLink[] | null = await safe(connection.sendRequest("textDocument/documentLink", {
        textDocument: td(model),
      } satisfies DocumentLinkParams));
      if (!r) return { links: [], dispose: noop };
      return {
        links: r.map((l) => ({ range: r2m(l.range), url: l.target, tooltip: l.tooltip })),
        dispose: noop,
      };
    },
  }));

  ldisp.push(monaco.languages.registerSignatureHelpProvider(LANGUAGE_ID, {
    signatureHelpTriggerCharacters: ["(", ","],
    async provideSignatureHelp(model, position) {
      if (!owns(model)) return null;
      const r: SignatureHelp | null = await safe(connection.sendRequest("textDocument/signatureHelp", {
        textDocument: td(model),
        position: m2p(position),
      } satisfies SignatureHelpParams));
      if (!r) return null;
      return {
        value: {
          signatures: r.signatures.map((s) => ({
            label: s.label,
            documentation: s.documentation as MonacoNs.IMarkdownString | string | undefined,
            parameters: (s.parameters ?? []).map((p) => ({
              label: p.label as string | [number, number],
              documentation: p.documentation as MonacoNs.IMarkdownString | string | undefined,
            })),
          })),
          activeSignature: r.activeSignature ?? 0,
          activeParameter: r.activeParameter ?? 0,
        },
        dispose: noop,
      };
    },
  }));

  ldisp.push(monaco.languages.registerDocumentSemanticTokensProvider(LANGUAGE_ID, {
    getLegend() {
      return { tokenTypes: [], tokenModifiers: [] };
    },
    async provideDocumentSemanticTokens(model) {
      if (!owns(model)) return null;
      const r: SemanticTokens | null = await safe(connection.sendRequest("textDocument/semanticTokens/full", {
        textDocument: td(model),
      } satisfies SemanticTokensParams));
      if (!r) return null;
      return { data: new Uint32Array(r.data), resultId: r.resultId };
    },
    releaseDocumentSemanticTokens() {},
  }));

  const handle: BridgeHandle = {
    worker,
    connection,
    syncClosedFile: (path: string, content: string): Promise<void> =>
      syncClosedFileVia(connection, path, content),
    dispose: () => {
      for (const d of ldisp) d.dispose();
      for (const w of watchers.values()) w.dispose();
      watchers.clear();
      void connection.sendNotification("exit").catch(noop);
      connection.dispose();
      worker.terminate();
    },
  };
  return handle;
}

export interface LspBridge {
  start(
    monaco: typeof MonacoNs,
    createWorker: CreateWorker,
    /** Composition id = the model URI authority this bridge owns. */
    wbId: string,
    initialFiles?: Record<string, string>,
    /** Errors-only mode: suppress non-error markers (TODO + graph warnings). */
    errorsOnly?: boolean,
  ): Promise<BridgeHandle>;
  dispose(): void;
}

export function createLspBridge(): LspBridge {
  let live: BridgeHandle | null = null;
  let bootPromise: Promise<BridgeHandle> | null = null;
  let disposed = false;

  return {
    async start(monaco, createWorker, wbId, initialFiles = {}, errorsOnly = false) {
      if (disposed) return Promise.reject(new Error("LSP bridge disposed"));
      if (live) {
        await reseedWorkspace(live, initialFiles);
        return live;
      }
      if (bootPromise) {
        const h = await bootPromise;
        await reseedWorkspace(h, initialFiles);
        return h;
      }
      bootPromise = bootBridge(monaco, createWorker, wbId, initialFiles, errorsOnly)
        .then((h) => {
          // Disposed mid-boot (unmount during the init window): tear the freshly
          // booted worker + its providers down instead of leaking them.
          if (disposed) {
            h.dispose();
            return h;
          }
          live = h;
          return h;
        })
        .catch((err: unknown) => {
          bootPromise = null; // allow a later retry instead of a stuck promise
          throw err;
        });
      return bootPromise;
    },
    dispose() {
      disposed = true;
      if (live) {
        live.dispose();
        live = null;
      }
      bootPromise = null;
    },
  };
}

// Reseed a live worker (re-mount / file-set change). `arch/setWorkspace` writes
// the VFS but doesn't recompile; the watched-files nudge makes the server
// re-read + republish so diagnostics are correct before the first edit.
async function reseedWorkspace(
  handle: BridgeHandle,
  files: Record<string, string>,
): Promise<void> {
  const keys = Object.keys(files);
  if (keys.length === 0) return;
  await handle.connection.sendNotification("arch/setWorkspace", { files, replace: true });
  const changed = keys.filter(
    (key) =>
      !key.startsWith("/__stdlib__") &&
      (key.endsWith(".arch") || key.endsWith(".archspace") || isMarkdownPath(key)),
  );
  if (changed.length === 0) return;
  await handle.connection.sendNotification("workspace/didChangeWatchedFiles", {
    changes: changed.map((key) => ({ uri: workspaceKeyToUri(key), type: 2 /* Changed */ })),
  });
}

/** The {@link BridgeHandle.syncClosedFile} implementation, connection-generic
 *  for testability: VFS merge write (NEVER `replace: true` — that wipes every
 *  other file the worker knows) + the watched-files nudge that makes the
 *  server re-read and recompile. */
export async function syncClosedFileVia(
  connection: Pick<MessageConnection, "sendNotification">,
  path: string,
  content: string,
): Promise<void> {
  const key = path.startsWith("/") ? path : "/" + path;
  await connection.sendNotification("arch/setWorkspace", {
    files: { [key]: content },
    replace: false,
  });
  await connection.sendNotification("workspace/didChangeWatchedFiles", {
    changes: [{ uri: workerUriForPath(key), type: 2 /* Changed */ }],
  });
}

/** Map a VFS key to the worker URI the server keyed it under — the same
 *  worker-side form the editor's translated `didOpen` uses, so a watched-file
 *  nudge overwrites rather than duplicates. */
export function workspaceKeyToUri(key: string): string {
  return workerUriForPath(key);
}

// ----- Helpers -------------------------------------------------------------
function noop(): void {}

async function safe<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (err) {
    if (err instanceof Error && /cancel|disposed|disconnected/i.test(err.message)) return null;
    console.warn("[arch-lsp] request failed", err);
    return null;
  }
}

/** Errors-only mode keeps only Error diagnostics (LSP severity 1), dropping
 *  warnings (graph/connectivity) + Info/Hint (TODO) noise. Identity passthrough
 *  when not in errors-only mode. */
export function filterDiagnostics(
  diagnostics: readonly Diagnostic[],
  errorsOnly: boolean,
): readonly Diagnostic[] {
  if (!errorsOnly) return diagnostics;
  return diagnostics.filter((d) => (d.severity ?? 1) === 1);
}

export function lspSeverityToMonaco(monaco: typeof MonacoNs, s: number): MonacoNs.MarkerSeverity {
  switch (s) {
    case 1: return monaco.MarkerSeverity.Error;
    case 2: return monaco.MarkerSeverity.Warning;
    case 3: return monaco.MarkerSeverity.Info;
    case 4:
    default: return monaco.MarkerSeverity.Hint;
  }
}

export function monacoSeverityToLsp(s: MonacoNs.MarkerSeverity): 1 | 2 | 3 | 4 {
  if (s >= 8) return 1;
  if (s >= 4) return 2;
  if (s >= 2) return 3;
  return 4;
}

export function normalizeHoverContents(h: Hover): MonacoNs.IMarkdownString[] {
  const raw = h.contents;
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map((c) => {
    if (typeof c === "string") return { value: c };
    if ("language" in c && "value" in c) return { value: "```" + c.language + "\n" + c.value + "\n```" };
    if ("kind" in c && "value" in c) return { value: c.value };
    return { value: String(c) };
  });
}

export function textEditToMonaco(e: TextEdit): MonacoNs.languages.TextEdit {
  return { range: r2m(e.range), text: e.newText };
}

// Map one LSP CompletionItem to a Monaco suggestion, carrying through every
// modern field (P2b). Monaco 0.52 supports all of them natively — `labelDetails`
// via `CompletionItemLabel` ({label,detail,description}) and `InsertReplaceEdit`
// via a `{insert,replace}` range — so nothing degrades on this surface.
export function completionItemToMonaco(
  monaco: typeof MonacoNs,
  it: CompletionItem,
): MonacoNs.languages.CompletionItem {
  const te = it.textEdit;
  // A plain TextEdit → a single replace range; an InsertReplaceEdit → Monaco's
  // {insert,replace} pair, so mid-word acceptance replaces to the word end
  // instead of degrading to Monaco's default word range.
  const range: MonacoNs.languages.CompletionItem["range"] = te
    ? "insert" in te
      ? { insert: r2m(te.insert), replace: r2m(te.replace) }
      : r2m(te.range)
    : (undefined as unknown as MonacoNs.IRange);
  // labelDetails → Monaco's rich CompletionItemLabel (parent path on the right).
  const label: MonacoNs.languages.CompletionItem["label"] = it.labelDetails
    ? { label: it.label, detail: it.labelDetails.detail, description: it.labelDetails.description }
    : it.label;
  const m: MonacoNs.languages.CompletionItem = {
    label,
    kind: (it.kind as unknown as MonacoNs.languages.CompletionItemKind) ??
      monaco.languages.CompletionItemKind.Text,
    insertText: it.insertText ?? it.label,
    insertTextRules: it.insertTextFormat === 2
      ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
      : undefined,
    detail: it.detail,
    documentation: it.documentation as MonacoNs.IMarkdownString | string | undefined,
    range,
    sortText: it.sortText,
    filterText: it.filterText,
    preselect: it.preselect,
    commitCharacters: it.commitCharacters,
    additionalTextEdits: it.additionalTextEdits?.map(textEditToMonaco),
    command: it.command
      ? { id: it.command.command, title: it.command.title, arguments: it.command.arguments }
      : undefined,
  };
  (m as { _lspData?: unknown })._lspData = it.data;
  return m;
}

// Translate a workspace edit's worker URIs back to this composition's model
// URIs, deduping by resolved resource + range + text. (Dedup also guards the
// historical case where the server keyed the same file under more than one URI
// string, which would make the bulk-edit service throw "Overlapping ranges".)
export function workspaceEditToMonaco(
  e: WorkspaceEdit,
  toModel: (workerUri: string) => MonacoNs.Uri,
): MonacoNs.languages.WorkspaceEdit {
  const edits: MonacoNs.languages.IWorkspaceTextEdit[] = [];
  const seen = new Set<string>();
  const add = (resource: MonacoNs.Uri, versionId: number | undefined, edit: TextEdit): void => {
    const r = edit.range;
    const key = `${resource.toString()}|${r.start.line}:${r.start.character}-${r.end.line}:${r.end.character}|${edit.newText}`;
    if (seen.has(key)) return;
    seen.add(key);
    edits.push({ resource, versionId, textEdit: textEditToMonaco(edit) });
  };
  if (e.changes) {
    for (const [uri, te] of Object.entries(e.changes)) {
      const resource = toModel(uri);
      for (const edit of te) add(resource, undefined, edit);
    }
  }
  if (e.documentChanges) {
    for (const dc of e.documentChanges) {
      if ("textDocument" in dc) {
        const resource = toModel(dc.textDocument.uri);
        for (const edit of dc.edits) add(resource, dc.textDocument.version ?? undefined, edit as TextEdit);
      }
    }
  }
  return { edits };
}

export function toMonacoSymbol(s: DocumentSymbol | SymbolInformation): MonacoNs.languages.DocumentSymbol {
  if ("range" in s) {
    return {
      name: s.name,
      detail: s.detail ?? "",
      kind: s.kind as unknown as MonacoNs.languages.SymbolKind,
      tags: (s.tags as unknown as MonacoNs.languages.SymbolTag[]) ?? [],
      range: r2m(s.range),
      selectionRange: r2m(s.selectionRange),
      children: s.children?.map(toMonacoSymbol) ?? [],
    };
  }
  return {
    name: s.name,
    detail: "",
    kind: s.kind as unknown as MonacoNs.languages.SymbolKind,
    tags: [],
    range: r2m(s.location.range),
    selectionRange: r2m(s.location.range),
    children: [],
  };
}
